/**
 * §7.4 盤點。
 * 盤點**只留軌跡、不改帳**——差異要以「調整（沖正）」處理（§9 規則 4：
 * 任何編輯都要實作成沖正事件）。這個設計常被誤解成「盤點沒用」，
 * 所以畫面上要把它講清楚。
 */

import { useEffect, useState } from 'react'
import { api } from '../api/client.js'
import { useMutation, useQuery } from '../api/hooks.js'
import { Alert, Button, Card, Empty, Field, KV, Mono, Select } from '../components/ui.js'
import { NumericKeypadInput } from '../components/NumericKeypadInput.js'
import { useDict } from '../config/ConfigContext.js'
import { count, kg, kgUnit } from '../../../shared/lib/format.js'

export function StockCount() {
  const dict = useDict()
  const stock = useQuery<{ items: Record<string, unknown>[] }>('/api/inventory/items?limit=300')
  const [itemId, setItemId] = useState('')
  const [countedKg, setCountedKg] = useState('')
  const [result, setResult] = useState<{ bookKg: number | null; countedKg: number; diffKg: number | null } | null>(null)

  const items = (stock.data?.items ?? []).filter((x) => x.status === 'in_stock')
  const item = items.find((x) => x.item_id === itemId)

  const post = useMutation<void, { bookKg: number | null; countedKg: number; diffKg: number | null }>(
    async (_a, key) => api.post('/api/stock-counts', {
      itemId, countedKg: Number(countedKg),
    }, { idempotencyKey: key }))

  useEffect(() => { if (!itemId && items[0]) setItemId(items[0].item_id as string) }, [items, itemId])

  return (
    <div className="space-y-4">
      <Card title="盤點" subtitle="記錄實地盤點的結果">
        <Alert tone="info" title="盤點只留軌跡，不改帳">
          盤到的數字會記在事件流裡（誰盤的、什麼時候盤的、盤到多少），但**不會覆寫帳上的重量**。
          帳要動只能用「調整（沖正）」，而且要填原因與核可人——這樣才查得出來每一次修正是誰做的、為什麼。
        </Alert>

        {items.length === 0 && !stock.loading && <Empty>目前沒有在庫料件可以盤。</Empty>}

        {items.length > 0 && (
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Field label="料件" required>
              <Select value={itemId} onChange={(e) => { setItemId(e.target.value); setResult(null) }}>
                {items.map((x) => (
                  <option key={x.item_id as string} value={x.item_id as string}>
                    {x.item_id as string} · {dict.material(x.material_code as string)} ·
                    {x.net_kg === null ? ' 無重量帳' : ` 帳面 ${kg(Number(x.net_kg), 1)} kg`}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="實盤重量（kg）" required>
              <NumericKeypadInput value={countedKg} decimals={1} keypad onChange={setCountedKg} />
            </Field>
          </div>
        )}

        {item && (
          <div className="mt-3 text-sm text-ink-600">
            帳面 {item.net_kg === null ? '（無重量帳）' : kgUnit(Number(item.net_kg), 1)} ·
            {' '}{count(item.panel_count as number)} 片
          </div>
        )}

        {post.error && <div className="mt-3"><Alert tone="danger">{post.error.message}</Alert></div>}

        {items.length > 0 && (
          <div className="mt-4">
            <Button variant="primary" size="lg" disabled={!itemId || !countedKg || post.pending}
              onClick={async () => {
                const r = await post.run()
                if (r) { setResult(r); setCountedKg(''); stock.reload() }
              }}>
              {post.pending ? '記錄中…' : '記錄盤點'}
            </Button>
          </div>
        )}
      </Card>

      {result && (
        <Card title="盤點已記錄" tone={result.diffKg !== null && Math.abs(result.diffKg) > 0.5 ? 'warn' : 'ok'}>
          <KV items={[
            ['帳面', result.bookKg === null ? '（無重量帳）' : kgUnit(result.bookKg, 1)],
            ['實盤', kgUnit(result.countedKg, 1)],
            ['差異', result.diffKg === null ? '無法計算' : (
              <strong key="d" className="tabular-nums">{kg(result.diffKg, 1)} kg</strong>
            )],
          ]} />
          {result.diffKg !== null && Math.abs(result.diffKg) > 0.5 && (
            <div className="mt-3">
              <Alert tone="warn" title="有差異，但帳沒有動">
                盤點只留軌跡。要修正帳務，請由有「調整（沖正）」權限的人處理，並填寫原因與核可人。
              </Alert>
            </div>
          )}
        </Card>
      )}
    </div>
  )
}
